import { FunctionComponent, JSX, useState } from 'react'
import styles from './sectionsTableRow.module.scss'
import { TableBody, TableCell, TableRow } from '@mui/material'
import { RegularSlot } from '@/types/Slots'
import { ErrorOutline, ExpandLess, ExpandMore } from '@mui/icons-material'
import { Daycare, DaycareType } from '@/types/Daycare'
import { useTranslation } from 'react-i18next'
import ChildTableRow from './childTableRow'
import { ChildWithParents } from '@/types/Child'
import Desktop from '@/modules/desktopMobile/desktop'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'

interface SectionsTableRowProps {
    childList: ChildWithParents[]
    daycare: Daycare | undefined
    section: string | null
    regularSlots: RegularSlot[]
    openSection: boolean
    canWriteRegular: boolean
    canReadRegular: boolean
    oneChildHaveSoftwareId: boolean
    selectedChildIds: number[]
    onToggleChild: (childId: number) => void
    onToggleSection: (childIds: number[], select: boolean) => void
}

const SectionsTableRow: FunctionComponent<SectionsTableRowProps> = ({
    childList,
    daycare,
    section,
    regularSlots,
    openSection,
    canWriteRegular,
    canReadRegular,
    oneChildHaveSoftwareId,
    selectedChildIds,
    onToggleChild,
    onToggleSection
}: SectionsTableRowProps) => {
    const { t } = useTranslation()

    const [showChildren, setShowChildren] = useState<boolean>(openSection)

    const isRpeOrLaep: boolean = daycare?.type === DaycareType.rpe || daycare?.type === DaycareType.laep

    function handleShowChildren(): void {
        setShowChildren(!showChildren)
    }

    const sectionChildIds: number[] = childList.map((child: ChildWithParents): number => child.id)
    const selectedInSection: number = sectionChildIds.filter((id: number) => selectedChildIds.includes(id)).length

    return (
        <TableBody className={styles.TableBody}>
            <TableRow className={styles.TableHeader} onClick={handleShowChildren}>
                <TableCell className={styles.SectionCell} align={'left'}>
                    <div className={styles.CheckboxContainer} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            checked={selectedInSection > 0 && selectedInSection === sectionChildIds.length}
                            indeterminate={selectedInSection > 0 && selectedInSection < sectionChildIds.length}
                            onChange={(checked: boolean) => onToggleSection(sectionChildIds, checked)}
                            className={styles.Checkbox}
                        />
                    </div>
                    {!showChildren ? (
                        <ExpandMore onClick={() => setShowChildren(true)} className={styles.Icon} />
                    ) : (
                        <ExpandLess onClick={() => setShowChildren(false)} className={styles.Icon} />
                    )}
                    {section ? (
                        section
                    ) : (
                        <>
                            {isRpeOrLaep ? t('withoutSector') : t('withoutSection')}
                            <ErrorOutline className={styles.IconNoSection} />
                        </>
                    )}
                </TableCell>
                <Desktop>
                    <TableCell />
                    {canReadRegular && <TableCell />}
                    {oneChildHaveSoftwareId && <TableCell />}
                    <TableCell />
                </Desktop>
            </TableRow>
            {showChildren &&
                daycare &&
                childList.map(
                    (child: ChildWithParents): JSX.Element => (
                        <ChildTableRow
                            key={child.id + regularSlots.length}
                            child={child}
                            daycare={daycare}
                            regularSlots={regularSlots.filter(
                                (regularSlot: RegularSlot): boolean => regularSlot.childId === child.id
                            )}
                            canWriteRegular={canWriteRegular}
                            canReadRegular={canReadRegular}
                            oneChildHaveSoftwareId={oneChildHaveSoftwareId}
                            selected={selectedChildIds.includes(child.id)}
                            onToggleSelect={onToggleChild}
                        />
                    )
                )}
        </TableBody>
    )
}

export default SectionsTableRow
